import {Layer} from "@/engine/layers/layer.js";
import {RasterLayer} from "@/engine/layers/rasterLayer.js";

export const LAYER_3D_SOURCE_TYPE_PLY = "ply";
export const LAYER_3D_SOURCE_TYPE_LAS = "las";
export const LAYER_3D_SOURCE_TYPE_LAZ = "laz";
export const LAYER_3D_SOURCE_TYPE_3DTILES = "3dtiles";
export const LAYER_3D_SOURCE_TYPE_3DTILES_11 = "3dtiles-1.1";
export const LAYER_3D_SCENE_TYPE_POINT_CLOUD = "pointCloud";
export const LAYER_3D_SCENE_TYPE_MESH = "mesh";

/**
 * Represents a 3D layer with an optional raster projection for the 2D Leaflet map.
 *
 * @extends RasterLayer
 */
export class Layer3D extends RasterLayer {
    /**
     * Creates a 3D layer model from a backend or local test structure.
     *
     * @param {object} struct Layer structure.
     * @param {string} struct.id Layer id.
     * @param {string} struct.name Layer name.
     * @param {object} struct.workspace Workspace owning this layer.
     * @param {string|object} struct.file 3D source file reference or legacy file field.
     * @param {string} [struct.sourceUrl] Direct URL to the 3D source file.
     * @param {string} [struct.sourceType] 3D source type: "ply", "las", "laz", "3dtiles", or "3dtiles-1.1".
     * @param {string} [struct.sceneType] 3D scene type: "pointCloud" or "mesh".
     * @param {number|string|object} [struct.zeroLevel] Optional fixed scene zero level for 3D reference helpers.
     * @param {object} [struct.geometry] Optional raster geometry used as a 2D map projection.
     * @returns {Layer3D} Initialized 3D layer instance.
     */
    constructor(struct) {
        super(struct);
        this.sourceUrl = struct.sourceUrl || this._sourceUrlFromFile(struct.file)
        this.sourceType = struct.sourceType || null
        this.sceneType = struct.sceneType || null
        this.zeroLevel = struct.zeroLevel ?? struct.sceneZeroLevel ?? null
        this.map3d = null
        this.layer3d = null
    }

    // temporary solution in order the layer has link to 3d viewer in debug purpose
    addOnMap3D(viewer, layer3d) {
        this.map3d = viewer
        this.layer3d = layer3d
    }
    /**
     * Adds the 2D raster projection to the Leaflet map when raster geometry exists.
     *
     * @param {L.Map} map Leaflet map receiving the optional raster projection.
     * @returns {void}
     */
    addOnMap(map) {
        if (this.hasRasterProjection()) {
            super.addOnMap(map);
            return;
        }

        Layer.prototype.addOnMap.call(this, map);
    }

    /**
     * Returns true when this 3D layer can draw a raster projection on the 2D map.
     *
     * @returns {boolean} True when raster geometry is present.
     */
    hasRasterProjection() {
        return Boolean(this.geometry);
    }

    /**
     * Returns the URL used by the 3D viewer to load the source object.
     *
     * @returns {string|null} 3D source URL, or null when it is not configured.
     */
    get3DSourceUrl() {
        return this.sourceUrl || null;
    }

    /**
     * Returns the 3D source type provided by the backend or local test layer config.
     *
     * @returns {string|null} Source type such as "ply", "las", "laz", "3dtiles", or "3dtiles-1.1"; null when unknown.
     */
    get3DSourceType() {
        return this.sourceType || null;
    }

    /**
     * Returns the 3D scene type provided by the backend or local test layer config.
     *
     * @returns {string|null} Scene type such as "pointCloud" or "mesh"; null when unknown.
     */
    get3DSceneType() {
        return this.sceneType || null;
    }

    /**
     * Returns a fixed 3D scene zero level provided by the backend or local test config.
     *
     * @returns {number|string|object|null} Explicit zero level, or null when dynamic detection should be used.
     */
    get3DZeroLevel() {
        return this.zeroLevel ?? null;
    }

    /**
     * Extracts a direct source URL from supported file metadata shapes.
     *
     * @param {string|{url?: string, href?: string, path?: string}|null|undefined} file File reference.
     * @returns {string|null} Extracted source URL, or null when the file is not usable.
     */
    _sourceUrlFromFile(file) {
        if (!file) {
            return null;
        }

        if (typeof file === "string") {
            return file;
        }

        return file.url || file.href || file.path || null;
    }

}
